import { useEffect, useState } from "react";
import { Search, AlertTriangle, CheckCircle2, XCircle, RefreshCw, DollarSign } from "lucide-react";
import api from "@/lib/api";

export default function SubscriptionList() {
  const [subs, setSubs] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/subscriptions", { params });
      setSubs(data);
    } catch { /* */ }
    setLoading(false);
  };

  useEffect(() => { fetch(); }, [statusFilter]);
  const handleSearch = (e) => { e.preventDefault(); fetch(); };

  const handleCancel = async (id) => {
    if (!window.confirm("Cancelar esta assinatura?")) return;
    await api.put(`/subscriptions/${id}`, { status: "cancelled" });
    fetch();
  };

  const today = new Date().toISOString().slice(0, 10);

  // Summary
  const activeCount = subs.filter((s) => s.status === "active").length;
  const overdueCount = subs.filter((s) => s.status === "active" && s.next_billing_date && s.next_billing_date < today).length;
  const cancelledCount = subs.filter((s) => s.status === "cancelled").length;
  const mrr = subs.filter((s) => s.status === "active").reduce((s, sub) => s + sub.monthly_amount, 0);

  const StatusBadge = ({ status, nextBilling }) => {
    const isOverdue = status === "active" && nextBilling && nextBilling < today;
    if (isOverdue) return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold"
        style={{ background: "rgba(239,68,68,0.15)", color: "#ef4444", border: "1.5px solid rgba(239,68,68,0.3)" }}>
        <AlertTriangle className="w-3.5 h-3.5" /> VENCIDA
      </span>
    );
    const config = {
      active: { icon: CheckCircle2, label: "ATIVA", bg: "rgba(16,185,129,0.12)", color: "#10b981", border: "rgba(16,185,129,0.25)" },
      cancelled: { icon: XCircle, label: "CANCELADA", bg: "rgba(148,163,184,0.12)", color: "#94a3b8", border: "rgba(148,163,184,0.25)" },
    };
    const c = config[status] || config.active;
    const Icon = c.icon;
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold"
        style={{ background: c.bg, color: c.color, border: `1.5px solid ${c.border}` }}>
        <Icon className="w-3.5 h-3.5" /> {c.label}
      </span>
    );
  };

  return (
    <div data-testid="subscription-list-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="crm-heading">Assinaturas</h1>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <div className="crm-card px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(16,185,129,0.1)", color: "#10b981" }}><CheckCircle2 className="w-5 h-5" /></div>
          <div><p className="text-xl font-bold" style={{ color: "var(--crm-text)" }}>{activeCount}</p><p className="text-xs" style={{ color: "var(--crm-text-muted)" }}>Ativas</p></div>
        </div>
        <div className="crm-card px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(239,68,68,0.1)", color: "#ef4444" }}><AlertTriangle className="w-5 h-5" /></div>
          <div><p className="text-xl font-bold" style={{ color: overdueCount > 0 ? "#ef4444" : "var(--crm-text)" }}>{overdueCount}</p><p className="text-xs" style={{ color: "var(--crm-text-muted)" }}>Vencidas</p></div>
        </div>
        <div className="crm-card px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(148,163,184,0.1)", color: "#94a3b8" }}><XCircle className="w-5 h-5" /></div>
          <div><p className="text-xl font-bold" style={{ color: "var(--crm-text)" }}>{cancelledCount}</p><p className="text-xs" style={{ color: "var(--crm-text-muted)" }}>Canceladas</p></div>
        </div>
        <div className="crm-card px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(6,182,212,0.1)", color: "#06b6d4" }}><DollarSign className="w-5 h-5" /></div>
          <div><p className="text-xl font-bold" style={{ color: "var(--crm-text)" }}>¥{mrr.toLocaleString()}</p><p className="text-xs" style={{ color: "var(--crm-text-muted)" }}>MRR</p></div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <form onSubmit={handleSearch} className="flex-1 relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--crm-text-dim)" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="subscription-search"
            placeholder="Buscar por cliente..." className="crm-input pl-10" />
        </form>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="subscription-status-filter"
          className="crm-input" style={{ maxWidth: "200px" }}>
          <option value="">Todos os status</option>
          <option value="active">Ativa</option>
          <option value="past_due">Vencida</option>
          <option value="cancelled">Cancelada</option>
        </select>
      </div>

      {/* Table */}
      <div className="crm-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="crm-table">
            <thead><tr><th>Cliente</th><th>Plano</th><th>Valor Mensal</th><th>Próx. Cobrança</th><th>Status</th><th className="text-right">Ações</th></tr></thead>
            <tbody>
              {loading ? (
                [...Array(3)].map((_, i) => <tr key={i}><td colSpan={6}><div className="h-6 rounded animate-pulse" style={{ background: "var(--crm-border)" }} /></td></tr>)
              ) : subs.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-14 text-base" style={{ color: "var(--crm-text-dim)" }}>Nenhuma assinatura encontrada</td></tr>
              ) : (
                subs.map((s) => {
                  const isOverdue = s.status === "active" && s.next_billing_date && s.next_billing_date < today;
                  return (
                    <tr key={s.id} data-testid={`sub-row-${s.id}`}
                      style={{ background: isOverdue ? "rgba(239,68,68,0.04)" : undefined }}>
                      <td className="primary" style={{ fontSize: "15px" }}>{s.customer_name}</td>
                      <td style={{ color: "var(--crm-text-secondary)", fontSize: "14px" }}>{s.product_name}</td>
                      <td style={{ color: "var(--crm-text)", fontWeight: 700, fontSize: "16px" }}>¥{s.monthly_amount.toLocaleString()}<span className="text-xs font-normal" style={{ color: "var(--crm-text-muted)" }}>/mês</span></td>
                      <td>
                        <span className="flex items-center gap-1.5 text-sm font-medium"
                          style={{ color: isOverdue ? "#ef4444" : "var(--crm-text-secondary)" }}>
                          {isOverdue && <AlertTriangle className="w-4 h-4" />}
                          {s.next_billing_date || "—"}
                        </span>
                      </td>
                      <td><StatusBadge status={s.status} nextBilling={s.next_billing_date} /></td>
                      <td className="text-right">
                        {s.status === "active" && (
                          <button onClick={() => handleCancel(s.id)}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition-all"
                            style={{ color: "#ef4444", background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.15)" }}>
                            <XCircle className="w-3.5 h-3.5" /> Cancelar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs mt-3" style={{ color: "var(--crm-text-dim)" }}>{subs.length} assinatura(s)</p>
    </div>
  );
}
